import { useState } from "react";
import { Download, Search, X } from "lucide-react";
import type { CreatureDefinition } from "../types";
import {
  creaturePreviewToDefinition,
  getSrdCreature,
  searchSrdCreatures,
  type SrdCreaturePreview,
  type SrdCreatureRuleset,
  type SrdCreatureSearchResult,
} from "../lib/srdCreatureApi";

export function SrdCreatureImporter({
  onClose,
  onImport,
  newId,
  now,
}: {
  onClose: () => void;
  onImport: (definition: CreatureDefinition) => void;
  newId: () => string;
  now: () => string;
}) {
  const [ruleset, setRuleset] = useState<SrdCreatureRuleset>("2024");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SrdCreatureSearchResult[]>([]);
  const [preview, setPreview] = useState<SrdCreaturePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function search() {
    setBusy(true);
    setError("");
    setPreview(null);

    try {
      setResults(await searchSrdCreatures(query, ruleset));
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Unable to reach the SRD API.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function choose(result: SrdCreatureSearchResult) {
    setBusy(true);
    setError("");

    try {
      setPreview(await getSrdCreature(result.index, ruleset));
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Unable to load that creature.",
      );
    } finally {
      setBusy(false);
    }
  }

  function doImport() {
    if (!preview) return;
    onImport(creaturePreviewToDefinition(preview, newId(), now()));
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal srd-modal"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <div className="eyebrow">OPEN SRD SOURCE</div>
            <h2>Import Creature from SRD</h2>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="notice">
          Imports create normal Worldwarden Creature Definitions. Your imported
          copy can be edited without changing or depending on the remote source.
        </div>

        <label className="field">
          <span>Rules source</span>
          <select
            value={ruleset}
            onChange={(event) => {
              setRuleset(event.target.value as SrdCreatureRuleset);
              setResults([]);
              setPreview(null);
            }}
          >
            <option value="2024">D&D 2024 — SRD 5.2</option>
            <option value="2014">D&D 2014 — SRD 5.1</option>
          </select>
        </label>

        <div className="srd-search">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void search();
              }
            }}
            placeholder='Try "dragon", "goblin", "owlbear"...'
          />
          <button className="primary" onClick={() => void search()} disabled={busy}>
            <Search size={16} />
            {busy ? "Loading..." : "Search"}
          </button>
        </div>

        {error && <div className="error-box">{error}</div>}

        <div className="srd-browser">
          <div className="srd-results">
            <div className="srd-pane-title">
              Search results <span>{results.length}</span>
            </div>
            {!results.length && (
              <p className="muted">Search the selected SRD monster collection.</p>
            )}
            {results.map((result) => (
              <button
                className="srd-result"
                key={result.index}
                onClick={() => void choose(result)}
              >
                <strong>{result.name}</strong>
                <span>{result.index}</span>
              </button>
            ))}
          </div>

          <div className="srd-preview">
            <div className="srd-pane-title">Preview</div>
            {!preview ? (
              <div className="srd-preview-empty">
                Select a creature to preview its SRD stat block before importing.
              </div>
            ) : (
              <>
                <div className="entity-top">
                  <span>
                    {preview.size} {preview.creatureType}
                  </span>
                  <span>{preview.ruleset} SRD</span>
                </div>
                <h3>{preview.name}</h3>
                <div className="srd-meta">
                  <span>
                    <b>AC</b>
                    {preview.armorClass ?? "—"}
                  </span>
                  <span>
                    <b>HP</b>
                    {preview.hitPoints ?? "—"}
                  </span>
                  <span>
                    <b>CR</b>
                    {preview.challengeRating ?? "—"}
                  </span>
                  <span>
                    <b>XP</b>
                    {preview.xp ?? "—"}
                  </span>
                </div>
                <p className="srd-copy">
                  {preview.actions.slice(0, 3).map((action) => (
                    <span key={action.name}>
                      <strong>{action.name}.</strong> {action.description}
                      {"\n\n"}
                    </span>
                  ))}
                </p>
                <div className="source-line">
                  Source: D&D {preview.ruleset} SRD via dnd5eapi.co
                </div>
                <button className="primary srd-import" onClick={doImport}>
                  <Download size={16} />
                  Import into Creature Master
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
